import { useMemo } from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { Item, Branch } from '@packages/shared-types';
import { LoadingLogo } from '@/components/shared/LoadingLogo';
import { useIndustryTranslation } from '@/hooks/useIndustryTranslation';

interface ItemsByBranchChartProps {
  items: Item[];
  branches: Branch[];
  isLoading?: boolean;
}

export function ItemsByBranchChart({
  items,
  branches,
  isLoading,
}: ItemsByBranchChartProps): JSX.Element {
  const noBranchDataLabel = useIndustryTranslation('pages.dashboard.charts.noBranchData');
  const chartData = useMemo(() => {
    // Group items by branchId
    const itemsByBranch = items.reduce(
      (acc, item) => {
        acc[item.branchId] = (acc[item.branchId] || 0) + 1;
        return acc;
      },
      {} as Record<string, number>,
    );

    // Map to branch names and sort by count
    const data = branches
      .map((branch) => ({
        name: branch.name.length > 15 ? `${branch.name.substring(0, 15)}...` : branch.name,
        fullName: branch.name,
        count: itemsByBranch[branch.id] || 0,
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10); // Limit to top 10 branches

    return data;
  }, [items, branches]);

  if (isLoading) {
    return (
      <div className="flex h-[300px] flex-col items-center justify-center gap-3">
        <LoadingLogo size="sm" />
        <span className="text-sm text-muted-foreground">Loading chart...</span>
      </div>
    );
  }

  if (chartData.length === 0) {
    return (
      <div className="flex h-[300px] items-center justify-center">
        <div className="text-sm text-muted-foreground">{noBranchDataLabel}</div>
      </div>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={300}>
      <BarChart data={chartData} layout="vertical">
        <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
        <XAxis type="number" className="text-xs" tick={{ fill: 'hsl(var(--muted-foreground))' }} />
        <YAxis
          type="category"
          dataKey="name"
          className="text-xs"
          width={100}
          tick={{ fill: 'hsl(var(--muted-foreground))' }}
        />
        <Tooltip
          contentStyle={{
            backgroundColor: 'hsl(var(--card))',
            border: '1px solid hsl(var(--border))',
            borderRadius: '0.5rem',
            color: 'hsl(var(--card-foreground))',
          }}
          formatter={(value?: number, _name?: string, props?: { payload?: { fullName?: string } }) => [
            value ?? 0,
            props?.payload?.fullName ?? '',
          ]}
        />
        <Bar dataKey="count" fill="hsl(var(--chart-primary))" radius={[0, 4, 4, 0]} />
      </BarChart>
    </ResponsiveContainer>
  );
}
